import React, { useEffect, useRef } from 'react';

interface Ripple {
    x: number;
    y: number;
    radius: number;
    maxRadius: number;
    speed: number;
    opacity: number;
}

export const WaterBackground: React.FC = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const animationFrameRef = useRef<number>();
    const timeRef = useRef(0);
    const ripplesRef = useRef<Ripple[]>([]);
    const mouseRef = useRef({ x: -1000, y: -1000, lastX: -1000, lastY: -1000 });
    const lastRippleTimeRef = useRef(0);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d', { alpha: false });
        if (!ctx) return;

        // Set canvas size
        const resizeCanvas = () => {
            const dpr = window.devicePixelRatio || 1;
            canvas.width = window.innerWidth * dpr;
            canvas.height = window.innerHeight * dpr;
            canvas.style.width = `${window.innerWidth}px`;
            canvas.style.height = `${window.innerHeight}px`;
            ctx.scale(dpr, dpr);
        };

        resizeCanvas();

        // Mouse move handler - create ripples
        const handleMouseMove = (e: MouseEvent) => {
            const currentTime = Date.now();
            mouseRef.current.x = e.clientX;
            mouseRef.current.y = e.clientY;

            // Create ripple every 80ms when mouse moves
            if (currentTime - lastRippleTimeRef.current > 80) {
                const dx = mouseRef.current.x - mouseRef.current.lastX;
                const dy = mouseRef.current.y - mouseRef.current.lastY;
                const distance = Math.sqrt(dx * dx + dy * dy);

                // Only create ripple if mouse actually moved
                if (distance > 3) {
                    ripplesRef.current.push({
                        x: e.clientX,
                        y: e.clientY,
                        radius: 0,
                        maxRadius: 120 + Math.random() * 40,
                        speed: 2.5 + Math.random() * 0.5,
                        opacity: 0.7,
                    });
                    lastRippleTimeRef.current = currentTime;
                    mouseRef.current.lastX = mouseRef.current.x;
                    mouseRef.current.lastY = mouseRef.current.y;
                }
            }
        };

        // Click handler - create larger ripple
        const handleClick = (e: MouseEvent) => {
            ripplesRef.current.push({
                x: e.clientX,
                y: e.clientY,
                radius: 0,
                maxRadius: 200,
                speed: 3.5,
                opacity: 0.9,
            });
        };

        // Water wave animation with interactive ripples
        const animate = () => {
            timeRef.current += 0.01;
            const width = window.innerWidth;
            const height = window.innerHeight;

            // Create gradient background (light lavender)
            const bgGradient = ctx.createLinearGradient(0, 0, 0, height);
            bgGradient.addColorStop(0, '#f5f3ff');
            bgGradient.addColorStop(1, '#ede9fe');
            ctx.fillStyle = bgGradient;
            ctx.fillRect(0, 0, width, height);

            // Draw multiple wave layers
            const waves = [
                { amplitude: 30, frequency: 0.003, speed: 0.5, yOffset: height * 0.3, color: 'rgba(139, 92, 246, 0.08)' },
                { amplitude: 40, frequency: 0.002, speed: 0.3, yOffset: height * 0.5, color: 'rgba(91, 76, 219, 0.06)' },
                { amplitude: 35, frequency: 0.0025, speed: 0.4, yOffset: height * 0.7, color: 'rgba(6, 182, 212, 0.05)' },
                { amplitude: 25, frequency: 0.0035, speed: 0.6, yOffset: height * 0.85, color: 'rgba(139, 92, 246, 0.04)' },
            ];

            waves.forEach((wave) => {
                ctx.beginPath();
                ctx.moveTo(0, wave.yOffset);

                // Create wave path
                for (let x = 0; x <= width; x++) {
                    const y = wave.yOffset +
                        Math.sin(x * wave.frequency + timeRef.current * wave.speed) * wave.amplitude +
                        Math.sin(x * wave.frequency * 1.5 + timeRef.current * wave.speed * 0.7) * (wave.amplitude * 0.5);
                    ctx.lineTo(x, y);
                }

                // Complete the wave shape
                ctx.lineTo(width, height);
                ctx.lineTo(0, height);
                ctx.closePath();

                // Fill with gradient
                const waveGradient = ctx.createLinearGradient(0, wave.yOffset - wave.amplitude, 0, height);
                waveGradient.addColorStop(0, wave.color);
                waveGradient.addColorStop(1, wave.color.replace(/[\d.]+\)/, '0.01)'));
                ctx.fillStyle = waveGradient;
                ctx.fill();
            });

            // Draw and update interactive ripples (same purple colors as waves)
            ripplesRef.current = ripplesRef.current.filter((ripple) => {
                ripple.radius += ripple.speed;
                ripple.opacity -= 0.01;

                if (ripple.opacity <= 0 || ripple.radius > ripple.maxRadius) {
                    return false;
                }

                // Draw ripple as expanding circle with gradient (purple theme)
                const innerRadius = Math.max(0, ripple.radius - 15);
                const gradient = ctx.createRadialGradient(
                    ripple.x, ripple.y, innerRadius,
                    ripple.x, ripple.y, ripple.radius + 5
                );
                gradient.addColorStop(0, `rgba(91, 76, 219, 0)`);
                gradient.addColorStop(0.4, `rgba(91, 76, 219, ${ripple.opacity * 0.25})`);
                gradient.addColorStop(0.7, `rgba(139, 92, 246, ${ripple.opacity * 0.3})`);
                gradient.addColorStop(1, `rgba(91, 76, 219, 0)`);

                ctx.fillStyle = gradient;
                ctx.beginPath();
                ctx.arc(ripple.x, ripple.y, ripple.radius, 0, Math.PI * 2);
                ctx.fill();

                // Draw ripple ring (purple)
                ctx.strokeStyle = `rgba(139, 92, 246, ${ripple.opacity * 0.5})`;
                ctx.lineWidth = 2;
                ctx.beginPath();
                ctx.arc(ripple.x, ripple.y, ripple.radius, 0, Math.PI * 2);
                ctx.stroke();

                // Inner ring for depth
                const innerRingRadius = Math.max(0, ripple.radius - 5);
                if (innerRingRadius > 0) {
                    ctx.strokeStyle = `rgba(91, 76, 219, ${ripple.opacity * 0.3})`;
                    ctx.lineWidth = 1;
                    ctx.beginPath();
                    ctx.arc(ripple.x, ripple.y, innerRingRadius, 0, Math.PI * 2);
                    ctx.stroke();
                }

                return true;
            });

            // Add subtle floating bubbles/particles
            const bubbleCount = 15;
            for (let i = 0; i < bubbleCount; i++) {
                const x = (width / bubbleCount) * i + Math.sin(timeRef.current * 0.5 + i) * 50;
                const y = height * 0.2 + Math.sin(timeRef.current * 0.3 + i * 0.5) * 100;
                const radius = 2 + Math.sin(timeRef.current + i) * 1;
                const opacity = 0.1 + Math.sin(timeRef.current * 0.5 + i) * 0.05;

                const bubbleGradient = ctx.createRadialGradient(x, y, 0, x, y, radius * 3);
                bubbleGradient.addColorStop(0, `rgba(91, 76, 219, ${opacity})`);
                bubbleGradient.addColorStop(1, 'rgba(91, 76, 219, 0)');

                ctx.fillStyle = bubbleGradient;
                ctx.beginPath();
                ctx.arc(x, y, radius * 3, 0, Math.PI * 2);
                ctx.fill();
            }

            animationFrameRef.current = requestAnimationFrame(animate);
        };

        // Start animation and add event listeners
        window.addEventListener('resize', resizeCanvas);
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('click', handleClick);
        animate();

        // Cleanup
        return () => {
            window.removeEventListener('resize', resizeCanvas);
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('click', handleClick);
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: -1,
            }}
        />
    );
};
